import type {FC, PropsWithChildren} from 'react';
import clsx from 'clsx';
import {getImageProps} from 'next/image';

import type {BreadcrumbItem} from '@/modules/foundation/components/breadcrumb';
import {Breadcrumb} from '@/modules/foundation/components/breadcrumb';
import {
	CallToAction,
	type CallToActionProps,
} from '@/modules/foundation/components/call-to-action/CallToAction';
import {Container} from '@/modules/foundation/components/container';
import {ImageBlock} from '@/modules/foundation/components/image/ImageBlock';
import type {ImageWeb} from '@/modules/foundation/components/image/types';
import {Heading} from '@/modules/foundation/components/typography/heading';
import type {PortableTextOverrides} from '@/modules/foundation/sanity/portable-text';

import {getBackgroundImage} from '../getBackgroundImage';

type Props = PropsWithChildren & {
	heading?: string;
	imageWeb?: ImageWeb;
	theme?: 'light' | 'dark';
	usePattern?: boolean;
	callToActionPrimary?: CallToActionProps;
	callToActionSecondary?: CallToActionProps;
	breadcrumb?: BreadcrumbItem[];
	showBreadcrumb?: boolean;
};

export const heroWithSideImagePortableTextOverrides: PortableTextOverrides = {
	callToActionLine: 'justify-center lg:justify-start',
	p: 'text-xl',
};

export const HeroWithSideImage: FC<Props> = ({
	heading,
	imageWeb,
	children,
	theme = 'dark',
	usePattern = true,
	callToActionPrimary,
	callToActionSecondary,
	breadcrumb,
	showBreadcrumb = false,
}) => {
	const {
		props: {srcSet},
	} = getImageProps({alt: '', width: 1960, height: 1024, src: '/images/finkn-pattern.svg'});

	const backgroundImage = getBackgroundImage(srcSet);

	return (
		<div
			className={clsx(
				'relative',
				'overflow-hidden',
				'bg-cover',
				'bg-center',
				theme === 'dark'
					? ['bg-primary-5', 'text-primary-95']
					: ['bg-grey-95', 'border-b', 'border-grey-border'],
			)}
			style={{backgroundImage: usePattern ? backgroundImage : undefined}}
		>
			{breadcrumb && showBreadcrumb && <Breadcrumb breadcrumb={breadcrumb} theme={theme} />}
			<Container className={clsx('py-10')}>
				<div
					className={clsx(
						'flex',
						'flex-col',
						'lg:flex-row',
						'gap-10',
						'md:gap-20',
						'items-center',
						'justify-between',
					)}
				>
					<div className={clsx('max-w-[630px]')}>
						<Heading spacing size="2xl">
							{heading}
						</Heading>
						{children && (
							<div
								className={clsx(
									theme === 'dark' ? 'text-secondary-70' : 'text-grey-15',
								)}
							>
								{children}
							</div>
						)}
						{(callToActionPrimary?.text || callToActionSecondary?.text) && (
							<div className={clsx('hidden', 'md:flex', 'gap-4', 'mt-10')}>
								{callToActionPrimary?.text && (
									<CallToAction
										{...callToActionPrimary}
										variant={theme === 'dark' ? 'defaultDark' : 'default'}
									/>
								)}
								{callToActionSecondary?.text && (
									<CallToAction
										{...callToActionSecondary}
										variant={theme === 'dark' ? 'secondaryDark' : 'secondary'}
									/>
								)}
							</div>
						)}
					</div>
					<div className={clsx('max-w-[635px]', 'w-full')}>
						{imageWeb && (
							<ImageBlock
								priority // Images in page heads are usually part of the LCP
								src={imageWeb.image.src}
								width={imageWeb.image.width}
								height={imageWeb.image.height}
								alt={imageWeb.altText ?? ''}
								className={clsx(
									'w-full',
									'rounded-tl',
									'rounded-tr-[107px]',
									'rounded-bl-[107px]',
									'rounded-br',
									'overflow-hidden',
								)}
							/>
						)}
						{(callToActionPrimary?.text || callToActionSecondary?.text) && (
							<div
								className={clsx('flex', 'md:hidden', 'flex-col', 'gap-4', 'mt-10')}
							>
								{callToActionPrimary?.text && (
									<CallToAction
										{...callToActionPrimary}
										variant={theme === 'dark' ? 'defaultDark' : 'default'}
									/>
								)}
								{callToActionSecondary?.text && (
									<CallToAction
										{...callToActionSecondary}
										variant={theme === 'dark' ? 'secondaryDark' : 'secondary'}
									/>
								)}
							</div>
						)}
					</div>
				</div>
			</Container>
		</div>
	);
};
